
import { defineComponent, onBeforeMount, PropType, reactive, watch } from 'vue'

// components
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { Client } from '@/new-services/types/orders'

// @services
import ordersAPI from '@/new-services/orders.service'

// composable
import useHelpers from '@/common/composable/useHelpers'

export default defineComponent({
    components: {
        TheSelect,
        TheButton,
    },
    props: {
        stepNumber: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        clientId: {
            type: Number,
            default: null,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const state = reactive({
            client: null as Client | null,
            client_id: null as number | null,
            is_client_selected: false,
        })

        const data_state = reactive({
            clients: new DataState<Client>({}),
        })

        //

        const loadClients = async (page: number, search?: string) => {
            data_state.clients.pagination.current_page = page

            try {
                data_state.clients.is_loading = true
                const response = await ordersAPI.getClients({
                    page,
                    limit: 50,
                    search,
                })
                data_state.clients.list = response.data.data

                // data_state.clients.pagination.total_pages = response.meta.last_page

                data_state.clients.is_failed = false
                data_state.clients.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.clients.failure.catchError(error)

                if (!data_state.clients.failure.error_state.is_cancel) {
                    data_state.clients.is_failed = true
                }
            } finally {
                if (!data_state.clients.failure.error_state.is_cancel) {
                    data_state.clients.is_loading = false
                }
            }
        }

        //

        const loadMoreClients = async (page: number) => {
            if (page > data_state.clients.pagination.total_pages) return
            data_state.clients.pagination.current_page = page

            try {
                data_state.clients.is_loading = true

                const params = {
                    page: page,
                    limit: 50,
                }
                const response = await ordersAPI.getClients(params)
                data_state.clients.list = data_state.clients.list.concat(response.data.data)

                data_state.clients.pagination.total_pages = response.data.meta.last_page

                data_state.clients.is_failed = false
                data_state.clients.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.clients.failure.catchError(error)

                if (!data_state.clients.failure.error_state.is_cancel) {
                    data_state.clients.is_failed = true
                }
            } finally {
                if (!data_state.clients.failure.error_state.is_cancel) {
                    data_state.clients.is_loading = false
                }
            }
        }

        const searchClients = (search_query: string) => {
            loadClients(1, search_query)
        }

        const unshiftFindedClient = async (client: Client) => {
            await loadClients(data_state.clients.pagination.current_page)
            data_state.clients.list.unshift(client)
        }

        //

        const onClientSelected = (client_id: number) => {
            const { findObjectByValue } = useHelpers()
            const client = findObjectByValue('id', client_id, data_state.clients.list)

            if (!client) return
            state.client = client
        }

        const selectClient = () => {
            state.is_client_selected = true
            emit('submit', { ...state.client })
        }

        const resetSelectedClient = () => {
            state.is_client_selected = false
            state.client = null
            state.client_id = null

            emit('reset')
        }

        //

        watch(
            () => props.clientId,
            (clientId) => {
                if (!clientId) {
                    state.is_client_selected = false
                    state.client_id = null
                    state.client = null
                }
            }
        )

        //

        onBeforeMount(() => {
            loadClients(1)
        })

        //

        return {
            state,
            data_state,
            //
            loadClients,
            loadMoreClients,
            searchClients,
            unshiftFindedClient,
            //
            onClientSelected,
            selectClient,
            resetSelectedClient,
        }
    },
})
